import React, { useEffect, useRef } from 'react';

import useAuth from 'hooks/useAuth';
import { useUserClient } from 'client/client-hook';
import { GetUserInfoReq, GetUserInfoReply } from 'proto/user-manager_pb';
import { isCountryPlaceholder } from 'data/countryPlaceholder';
import { config } from 'env';

import { isAnalyticsEnabled } from './active-client';
import { analytics } from './analytics';

// ==============================|| ANALYTICS — PROVIDER ||============================== //
// Side-effect only: it renders children untouched and keeps PostHog's person in
// step with the logged-in user. The SDK itself is initialised at boot (index.tsx)
// and the first identify happens in Auth0Context the moment the session resolves;
// this provider layers the profile traits, country and billing value onto that
// same person. distinct_id is the Auth0 subject everywhere — flag bucketing
// hashes it, so it must never be swapped for another id.
//
// Mount it INSIDE the auth provider so useAuth resolves.

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const { user } = useAuth();
  const userClient = useUserClient();

  // Latest user without putting every field in the effect deps.
  const userRef = useRef(user);
  userRef.current = user;

  // Which id we have already identified, so a re-render doesn't re-send.
  const identifiedId = useRef<string | null>(null);

  const subject = user?.sub;
  const tcId = user?.tcId;

  useEffect(() => {
    if (subject) {
      if (identifiedId.current === subject) return;
      identifiedId.current = subject;

      const current = userRef.current;
      analytics.identifyUser(subject, {
        email: current?.email,
        name: current?.name,
        currency: current?.currency,
        timezone: current?.timezone,
        tc_id: current?.tcId,
        env: config.env_name
      });
      return;
    }

    // No user: only reset if we had previously identified someone (logout).
    if (identifiedId.current) {
      identifiedId.current = null;
      analytics.reset();
    }
  }, [subject]);

  // Country is the one breakdown the server-side snapshots use, but it is not in
  // the Auth0 token — user-manager owns it. Fetched once per user and attached as
  // a person property, so it lands on every event the SDK sends afterwards.
  const countryFetchedFor = useRef<string | null>(null);

  // useUserClient returns a new callback whenever the access token changes
  // (e.g. after a refresh). As an effect dep that would cancel the in-flight
  // request, and the once-per-user guard would then drop the reply — so it is
  // read through a ref.
  const userClientRef = useRef(userClient);
  userClientRef.current = userClient;

  useEffect(() => {
    if (!isAnalyticsEnabled || !subject || !tcId || countryFetchedFor.current === tcId) return;
    countryFetchedFor.current = tcId;

    let cancelled = false;
    (async () => {
      try {
        const client = await userClientRef.current();
        const req: GetUserInfoReq.AsObject = { userId: tcId };
        const res = (await client.getUserInfo(req)) as GetUserInfoReply.AsObject;
        // Older profiles can hold the picker's placeholder instead of a country.
        const country = res?.country?.trim();
        // A late reply for a user who has since logged out must not re-identify them.
        if (!cancelled && country && !isCountryPlaceholder(country) && identifiedId.current === subject) {
          analytics.identifyUser(subject, { country });
        }
      } catch {
        // Analytics must never surface an error to the page; country is optional.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [subject, tcId]);

  return <>{children}</>;
};

export default AnalyticsProvider;
